// src/components/core/MigrationModal/MigrationTokenFormModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { KeyOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import migrationTokenStep from './Step1';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Token format: alphanumeric, hyphen, dot (e.g., "migrace.invoices.4c46306a-6c85-41bd-a485-826ae7d939b7")
const TOKEN_REGEX = /^[a-z0-9\-\.]+$/i;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Validate migration token format (local validation)
 * @param {string} token - The token to validate
 * @returns {{ valid: boolean, message?: string }}
 */
const validateMigrationToken = token => {
  if (!token || typeof token !== 'string') {
    return { valid: false, message: translateX('integration_token_required') };
  }

  const trimmed = token.trim();
  const length = trimmed.length;

  // Token must be between 50 and 80 characters
  if (length < 50 || length > 80) {
    return { valid: false, message: translateX('integration_token_invalid_length') };
  }

  // Token must match allowed format (alphanumeric, hyphen, dot)
  if (!TOKEN_REGEX.test(trimmed)) {
    return { valid: false, message: translateX('integration_token_invalid_format') };
  }

  return { valid: true };
};

/**
 * Validate token against backend API for a specific module
 * @param {string} module - The module name (e.g., 'invoices', 'products')
 * @param {string} token - The token to validate
 * @returns {Promise<Object>} Validation result with scope info or error
 */
const validateTokenOnServer = async (module, token) => {
  try {
    const [doc, result] = await axiosService.post({
      url: API.MIGRATION.VALIDATE_TOKEN,
      data: { module, token },
      returnAsObject: true,
    });

    // Check result.success (not doc.success) - axiosService returns [doc, result]
    if (result?.success && doc) {
      return {
        valid: true,
        scope: doc.scope,
        collectionName: doc.collectionName,
        sourceInfo: doc.sourceInfo,
        totalRecords: doc.totalRecords,
        enrich: doc.enrich,
        validatedAt: doc.validatedAt,
      };
    }

    // Handle error response from successful HTTP call (HTTP 200 but logical error)
    const errorCode = doc?.errorCode || result?.errorCode;
    return {
      valid: false,
      errorCode,
      error: getErrorMessageByCode(errorCode, module),
    };
  } catch (error) {
    console.error('[MigrationTokenFormModal] Token validation error:', error);

    // Handle specific error codes from HTTP error response (4xx, 5xx)
    const errorData = error.response?.data?.data || error.response?.data || {};
    const errorCode = errorData?.errorCode;

    return {
      valid: false,
      errorCode: errorCode || 'UNKNOWN_ERROR',
      error: getErrorMessageByCode(errorCode, module, errorData),
    };
  }
};

/**
 * Get translated error message by error code
 * @param {string} errorCode - The error code from backend
 * @param {string} module - The module name for context
 * @param {Object} errorData - Additional error data for interpolation
 * @returns {string} Translated error message
 */
function getErrorMessageByCode(errorCode, module, errorData = {}) {
  switch (errorCode) {
    case 'TOKEN_NOT_FOUND':
      return translateX('integration_token_not_found');

    case 'MODULE_MISMATCH':
      return translateX('integration_token_module_mismatch', {
        expectedModule: errorData?.expectedModule || module,
        actualModule: errorData?.actualModule || 'unknown',
      });

    case 'INVALID_MODULE':
      return translateX('integration_token_invalid_module');

    default:
      return translateX('integration_token_validation_error');
  }
}

/**
 * Mask a token for display (shows first part + last 4 characters)
 * e.g., "migrace.invoices.4c46306a-6c85-41bd-a485-826ae7d939b7" -> "migrace.invo...b7"
 * @param {string} token - The plain token
 * @returns {string} Masked token
 */
const maskMigrationToken = token => {
  if (!token || token.length < 10) return '';

  // Find the first dot or hyphen to preserve the prefix
  const firstSeparator = token.search(/[.\-]/);

  if (firstSeparator > 0 && firstSeparator < 20) {
    // Has prefix like "migrace." - show prefix + first 4 chars + ... + last 3
    const prefix = token.substring(0, firstSeparator + 1);
    const afterPrefix = token.substring(firstSeparator + 1);
    const firstChars = afterPrefix.substring(0, 4);
    const lastChars = token.slice(-3);
    return `${prefix}${firstChars}...${lastChars}`;
  }

  // No clear prefix - show first 8 + ... + last 4
  const firstChars = token.substring(0, 8);
  const lastChars = token.slice(-4);
  return `${firstChars}...${lastChars}`;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const MigrationTokenFormModal = ({ show, setShow, afterClose, onSuccess, module = 'invoices' }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, goFocus, setLoading }) => {
    // Activate loading IMMEDIATELY to prevent double-click
    setLoading?.(true);

    // Message key for updates
    const msgKey = 'migration-token-save';

    // Create persistent message for the entire process
    appService.message({
      flag: 'loading',
      text: translateX('validating_token'),
      duration: 0,
      key: msgKey,
    });

    try {
      const token = form.getFieldValue('migrationToken');

      // 1. Local format validation
      const localValidation = validateMigrationToken(token);

      if (!localValidation.valid) {
        appService.message({ flag: 'w', text: localValidation.message, duration: 3, key: msgKey });
        goFocus?.('migrationToken', true);
        return false;
      }

      const trimmedToken = token.trim();

      // 2. Validate token on server with module
      const serverValidation = await validateTokenOnServer(module, trimmedToken);

      if (!serverValidation.valid) {
        appService.message({ flag: 'e', text: serverValidation.error, duration: 3, key: msgKey });
        goFocus?.('migrationToken', true);
        return false;
      }

      // 3. Save token to database
      appService.message({
        flag: 'loading',
        text: translateX('saving_token'),
        duration: 0,
        key: msgKey,
      });

      const [doc, result] = await axiosService.post({
        url: API.MIGRATION.SAVE_TOKEN,
        data: {
          module,
          token: trimmedToken,
          name: serverValidation.sourceInfo?.clientName || `Token ${module}`,
        },
        returnAsObject: true,
      });

      if (!result?.success && !doc?.success) {
        appService.message({
          flag: 'e',
          text: translateX('token_save_failed'),
          duration: 3,
          key: msgKey,
        });
        return false;
      }

      // Token saved successfully - show success message with scope name
      const scopeName = serverValidation.sourceInfo?.clientName || serverValidation.scope;
      appService.message({
        flag: 's',
        text: scopeName
          ? translateX('token_validated_for', { clientName: scopeName })
          : translateX('token_saved_successfully'),
        duration: 3,
        key: msgKey,
      });

      // Call success callback with all data including tokenId from save response
      if (onSuccess) {
        await onSuccess({
          tokenId: doc?.tokenId || doc?._id,
          token: trimmedToken,
          tokenMasked: maskMigrationToken(trimmedToken),
          scope: serverValidation.scope,
          collectionName: serverValidation.collectionName,
          sourceInfo: serverValidation.sourceInfo,
          totalRecords: serverValidation.totalRecords,
          enrich: serverValidation.enrich,
          validatedAt: serverValidation.validatedAt,
        });
      }

      return true;
    } catch (error) {
      console.error('[MigrationTokenFormModal] Error saving token:', error);
      appService.message({
        flag: 'e',
        text: translateX('token_save_failed'),
        duration: 3,
        key: msgKey,
      });
      return false;
    } finally {
      setLoading?.(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(() => [migrationTokenStep()], []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<KeyOutlined />}
      title="integration_token"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={480}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MigrationTokenFormModal;
export { maskMigrationToken, validateMigrationToken };
